import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { orderApi } from '@formosa/shared/api/order';
import { ApiError } from '@formosa/shared/api/request';
import SettingsSubPageShell from '../../components/settings/SettingsSubPageShell';
import TradeConfirmModal from '../../components/trade/TradeConfirmModal';
import FollowOrderSuccessModal from '../../components/trade/FollowOrderSuccessModal';
import { useWallet } from '../../context/WalletContext';
import { useTranslation } from '../../i18n/LocaleContext';
import {
  computeRemainingPercentAmount,
  formatFollowAmount,
  formatFollowPeriodLabel,
  isManualFollowAmount,
  isPercentFollowAmount,
  parseManualAmountRange,
} from '../../utils/followOrder';

export default function FollowOrderConfirmPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { followOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const symbolParam = searchParams.get('symbol');
  const { balance, refreshWallet } = useWallet();
  const { t } = useTranslation();

  const [followOrder, setFollowOrder] = useState(location.state?.followOrder || null);
  const [loading, setLoading] = useState(!location.state?.followOrder);
  const [error, setError] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const manualRange = useMemo(
    () => parseManualAmountRange(followOrder?.amountValue),
    [followOrder?.amountValue]
  );

  const isManual = isManualFollowAmount(followOrder?.amountType);
  const isPercent = isPercentFollowAmount(followOrder?.amountType);

  const resolvedAmount = useMemo(() => {
    if (!followOrder) {
      return 0;
    }
    if (isPercent) {
      return computeRemainingPercentAmount(balance, followOrder.amountValue);
    }
    return Number(manualAmount) || 0;
  }, [balance, followOrder, isPercent, manualAmount]);

  const amountValid = useMemo(() => {
    if (!followOrder || resolvedAmount <= 0) {
      return false;
    }
    if (!isManual) {
      return true;
    }
    return resolvedAmount >= manualRange.min && resolvedAmount <= manualRange.max;
  }, [followOrder, isManual, manualRange.max, manualRange.min, resolvedAmount]);

  useEffect(() => {
    refreshWallet();
  }, [refreshWallet]);

  useEffect(() => {
    if (followOrder || !followOrderId) {
      return;
    }
    let cancelled = false;

    async function loadFollowOrder() {
      setLoading(true);
      setError('');
      try {
        const page = await orderApi.listFollowOrders({ pageNo: 1, pageSize: 100 });
        const matched = (page?.records || []).find((item) => String(item.id) === String(followOrderId));
        if (cancelled) {
          return;
        }
        if (!matched) {
          setError(t('follow.loadFailed'));
          return;
        }
        setFollowOrder(matched);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t('follow.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadFollowOrder();
    return () => {
      cancelled = true;
    };
  }, [followOrder, followOrderId, t]);

  function buildReturnPath() {
    return symbolParam
      ? `/trade/futures?symbol=${encodeURIComponent(symbolParam)}`
      : '/trade/futures';
  }

  function handleSuccessClose() {
    setSuccessOpen(false);
    navigate(buildReturnPath(), { replace: true });
  }

  async function handlePlaceOrder() {
    if (!followOrder?.id || !amountValid || submitting) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = { followOrderId: followOrder.id };
      if (isManual) {
        payload.orderAmount = resolvedAmount;
      }
      await orderApi.placeFuturesOrder(payload);
      setConfirmOpen(false);
      setSuccessOpen(true);
      refreshWallet();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('follow.placeFailed'));
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  const confirmMessage = t('follow.confirmMessage').replace(
    '{amount}',
    formatFollowAmount(resolvedAmount)
  );

  return (
    <SettingsSubPageShell title={t('follow.deliveryTitle')}>
      <div className="follow-confirm-page">
        {loading && <p className="follow-confirm-page__status">{t('common.loading')}</p>}
        {error && <p className="follow-confirm-page__error">{error}</p>}

        {followOrder && (
          <>
            <div className="follow-confirm-page__card">
              <div className="follow-confirm-page__row">
                <span>{t('follow.name')}</span>
                <strong>{followOrder.name || t('common.dash')}</strong>
              </div>
              <div className="follow-confirm-page__row">
                <span>{t('follow.period')}</span>
                <strong>{formatFollowPeriodLabel(followOrder.periodType)}</strong>
              </div>
              {isManual && (
                <>
                  <div className="follow-confirm-page__row">
                    <span>{t('follow.minFollowAmount')}</span>
                    <strong>{formatFollowAmount(manualRange.min)}</strong>
                  </div>
                  <div className="follow-confirm-page__row">
                    <span>{t('follow.maxFollowAmount')}</span>
                    <strong>{formatFollowAmount(manualRange.max)}</strong>
                  </div>
                </>
              )}
            </div>

            {isManual ? (
              <div className="follow-confirm-page__amount-card">
                <label className="follow-confirm-page__amount-label" htmlFor="followManualAmount">
                  {t('follow.followAmount')}
                </label>
                <div className="follow-confirm-page__amount-input-wrap">
                  <input
                    id="followManualAmount"
                    className="follow-confirm-page__amount-input"
                    inputMode="decimal"
                    value={manualAmount}
                    placeholder="0"
                    onChange={(event) => setManualAmount(event.target.value.replace(/[^\d.]/g, ''))}
                  />
                  {manualAmount && (
                    <button
                      type="button"
                      className="follow-confirm-page__clear"
                      aria-label={t('common.close')}
                      onClick={() => setManualAmount('')}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="follow-confirm-page__amount-card">
                <div className="follow-confirm-page__amount-label">{t('follow.followAmount')}</div>
                <div className="follow-confirm-page__amount-value">
                  {formatFollowAmount(resolvedAmount)}
                </div>
              </div>
            )}

            <button
              type="button"
              className={`follow-confirm-page__submit ${amountValid ? 'is-ready' : ''}`}
              disabled={!amountValid || submitting}
              onClick={() => setConfirmOpen(true)}
            >
              {t('follow.oneClick')}
            </button>
          </>
        )}
      </div>

      <TradeConfirmModal
        open={confirmOpen}
        title={t('follow.confirmTitle')}
        message={confirmMessage}
        confirming={submitting}
        onClose={() => !submitting && setConfirmOpen(false)}
        onConfirm={handlePlaceOrder}
      />

      <FollowOrderSuccessModal open={successOpen} onClose={handleSuccessClose} />
    </SettingsSubPageShell>
  );
}
